import { useMemo, useState } from 'react'

import { useFloorPlan } from '../../hooks/useFloorPlan'
import { useBulkPrint } from '../hooks/useBulkPrint'
import { useIsEditMode } from '@/hooks/useIsEditMode'
import { useIsEditModeSatisfied } from '@/hooks/useIsEditModeSatisfied'
import { usePermissions } from '@/hooks/usePermissions'
import { useTranslation } from 'react-i18next'
import { ImPlus } from 'react-icons/im'
import { MdPrint, MdQrCode } from 'react-icons/md'
import { useSelector } from 'react-redux'

import {
  Box,
  Checkbox,
  Flex,
  HStack,
  Text,
  Tooltip,
  VStack,
  useDisclosure,
} from '@chakra-ui/react'
import { AnimatePresence, motion } from 'framer-motion'

import {
  Button,
  DragEventI,
  DrawerScrollContainer,
  SearchInput,
  Switch,
} from '@/components/ui'
import { CheckpointIProps } from '@/features/floor-plan'
import {
  CheckpointListItem,
  UnPlacedCheckpointListItem,
} from '@/features/guard-tour'
import { selectMe } from '@/redux/me/meSlice'
import { BUTTON_PRESS, trackEvent } from '@/utils/analytics'
import { tVar } from '@/utils/i18n'

import {
  getPlacedCheckpoints,
  getUnPlacedCheckpoints,
} from '../../utils/filterCheckpoints'
import { BulkPrintModal } from './BulkPrintModal'
import { CreateCheckPointsModal } from './CreateCheckPointsModal'
import { NoCheckPointView } from './NoCheckPointView'

interface CheckPointsPanelIProps {
  facilityId: string
  checkpoints: CheckpointIProps[]
  onTileDragStart: (event: DragEventI) => void
  onCheckPointClick: (checkpoint: CheckpointIProps) => void
  onDeviceItemHoverIn: (deviceId: string) => void
  onDeviceItemHoverOut: () => void
}

export const CheckPointList = ({
  facilityId,
  checkpoints,
  onCheckPointClick,
  onTileDragStart,
  onDeviceItemHoverIn,
  onDeviceItemHoverOut,
}: CheckPointsPanelIProps) => {
  const { t } = useTranslation()
  const me = useSelector(selectMe)
  // Old Edit Mode lock - keep for now as Edit Mode being on blocks things from being dropped onto floor plan
  const isEditModeOn = useIsEditMode()
  // New Edit Mode Lock OR feature flags
  const isEditModeSatisfied = useIsEditModeSatisfied()
  const { shouldEnableCreateCheckpoint } = usePermissions()
  const {
    isOpen: isModalOpen,
    onOpen: onModalOpen,
    onClose: onModalClose,
  } = useDisclosure()
  const {
    isOpen: isPrintModalOpen,
    onOpen: onPrintModalOpen,
    onClose: onPrintModalClose,
  } = useDisclosure()
  const { floorId, isFloorPlanKnownEmpty } = useFloorPlan()
  // Only a CONFIRMED empty floor blocks creation; loading/error states keep
  // the button enabled rather than misreporting "no floor plan" (ENG-33744).
  const hasFloorPlan = !isFloorPlanKnownEmpty
  const [shouldShowPlaced, setShowPlaced] = useState(true)
  const [filter, setFilter] = useState('')

  const placedCheckpoints: CheckpointIProps[] = useMemo(() => {
    return getPlacedCheckpoints(checkpoints, filter, floorId)
  }, [checkpoints, filter, floorId])

  const unplacedCheckpoints: CheckpointIProps[] = useMemo(() => {
    return getUnPlacedCheckpoints(checkpoints, filter)
  }, [checkpoints, filter])

  // Visible checkpoints based on current filter state
  const visibleCheckpoints: CheckpointIProps[] = useMemo(() => {
    if (shouldShowPlaced) {
      return [...unplacedCheckpoints, ...placedCheckpoints]
    }
    return unplacedCheckpoints
  }, [shouldShowPlaced, unplacedCheckpoints, placedCheckpoints])

  // Bulk print functionality - uses visible checkpoints for Select All behavior
  const {
    selectedCheckpoints,
    isSelected,
    toggleCheckpoint,
    toggleSelectAll,
    isAllSelected,
    isIndeterminate,
    selectedCount,
  } = useBulkPrint(visibleCheckpoints)

  const hasPlacedCheckpoint = placedCheckpoints.length > 0
  const hasUnplacedCheckpoint = unplacedCheckpoints.length > 0

  const onToggleChange = () => {
    setShowPlaced(!shouldShowPlaced)
  }

  const handleOnClick = (checkpoint: CheckpointIProps) => {
    onCheckPointClick(checkpoint)
  }

  return (
    <>
      <Flex
        align='center'
        borderBottom='1px solid rgba(9, 30, 66, 0.14)'
        direction='column'
        padding='30px'
      >
        <VStack alignItems='start' spacing='10px' w='full'>
          <HStack>
            <MdQrCode color='#44546F' size={26} />
            <Text
              color='#1F2023'
              fontSize='20px'
              fontWeight={700}
              lineHeight='30px'
            >
              {t('floors:checkpoint_list_title')}
            </Text>
          </HStack>
          <Text
            color='#1F2023'
            fontSize='12px'
            fontWeight={300}
            lineHeight='18px'
            pb='5px'
          >
            {shouldEnableCreateCheckpoint
              ? t('floors:checkpoint_list_description_admin')
              : t('floors:checkpoint_list_description')}
          </Text>
          <HStack spacing={2} w='full'>
            <Tooltip
              isDisabled={hasFloorPlan}
              label={t('floors:checkpoint_create_no_floor_plan_tooltip')}
              placement='bottom'
            >
              {/* The box keeps the tooltip firing while the button is disabled;
                  the conditional tabIndex lets keyboard users focus it to read
                  the reason (Chakra shows the tooltip on focus). */}
              <Box
                display='flex'
                flex={1}
                tabIndex={hasFloorPlan ? undefined : 0}
              >
                <Button
                  flex={1}
                  isDisabled={
                    !isEditModeSatisfied ||
                    !shouldEnableCreateCheckpoint ||
                    !hasFloorPlan
                  }
                  justifyContent='flex-start'
                  leftIcon={
                    <Box mr='15px'>
                      <ImPlus size={12} />
                    </Box>
                  }
                  onClick={() => {
                    onModalOpen()
                    trackEvent(
                      BUTTON_PRESS +
                        ' Facility Checkpoints - Create Checkpoint',
                      {
                        facility_id: facilityId,
                        role: me.roleName,
                      }
                    )
                  }}
                  variant='affirm'
                >
                  {t('floors:checkpoint_create_button')}
                </Button>
              </Box>
            </Tooltip>
            <Button
              data-testid='checkpointList_printButton'
              isDisabled={checkpoints.length === 0}
              leftIcon={<MdPrint size={16} />}
              onClick={() => {
                onPrintModalOpen()
                trackEvent(
                  BUTTON_PRESS + ' Facility Checkpoints - Print QR Codes',
                  {
                    facility_id: facilityId,
                    role: me.roleName,
                    selected_count: selectedCount,
                    total_count: checkpoints.length,
                  }
                )
              }}
              size='sm'
              variant='outline'
            >
              {selectedCount > 0
                ? tVar(t, 'floors:checkpoint_print_selected', {
                    selectedCount,
                  })
                : t('floors:checkpoint_print_all')}
            </Button>
          </HStack>
        </VStack>
      </Flex>
      <DrawerScrollContainer mb='40px' px='30px' py='20px'>
        <Flex alignItems='center' justifyContent='space-between'>
          <Box alignItems='center' display='flex'>
            <Switch
              backgroundColor='#2fb2b0'
              ballHeight='12px'
              ballPadding='2px'
              checked={!shouldShowPlaced}
              dataTestId='Switch:input'
              height='16px'
              onChange={onToggleChange}
              width='40px'
            />
            <Box
              color='#667289'
              fontSize='11px'
              fontWeight={500}
              lineHeight='16px'
              ml='10px'
            >
              {t('floors:checkpoint_show_unplaced')}
            </Box>
          </Box>
          {checkpoints.length > 0 && (
            <Checkbox
              colorScheme='blue'
              data-testid='checkpointList_selectAll'
              isChecked={isAllSelected}
              isIndeterminate={isIndeterminate}
              onChange={toggleSelectAll}
              size='sm'
            >
              <Text fontSize='11px'>{t('common:select_all')}</Text>
            </Checkbox>
          )}
        </Flex>
        <Box mb='20px' mt='20px'>
          <SearchInput
            onChange={(e) => setFilter(e.target.value)}
            placeholder={t(
              'floors:checkpoint_filter_by_name_or_code_placeholder'
            )}
            showIcon={false}
            value={filter}
          />
        </Box>
        <AnimatePresence>
          {[...unplacedCheckpoints, ...placedCheckpoints].length > 0 ? (
            <>
              {shouldShowPlaced && placedCheckpoints.length > 0 && (
                <Box
                  animate={{ opacity: 1 }}
                  as={motion.div}
                  exit={{ opacity: 0 }}
                  initial={{ opacity: 0 }}
                  w='full'
                >
                  <Text
                    color='#44546F'
                    fontSize='16px'
                    fontWeight={500}
                    letterSpacing='-0.4px'
                    lineHeight='24px'
                    my='20px'
                  >
                    {t('floors:checkpoint_current_floor')}
                  </Text>
                  <VStack align='stretch' spacing='10px'>
                    {hasPlacedCheckpoint ? (
                      placedCheckpoints.map((c) => (
                        <Flex alignItems='center' gap={2} key={c.id}>
                          <Checkbox
                            colorScheme='blue'
                            isChecked={isSelected(c.id)}
                            onChange={() => toggleCheckpoint(c.id)}
                            size='sm'
                          />
                          <Box flex={1}>
                            <CheckpointListItem
                              id={c.id}
                              // We want this to be the new edit mode check
                              // becaue it simply enables the ability to click
                              // the edit pencil - not to drag out into the floor
                              isEditMode={isEditModeSatisfied}
                              label={c.name}
                              onClick={() => handleOnClick(c)}
                              onDeviceItemHoverIn={onDeviceItemHoverIn}
                              onDeviceItemHoverOut={onDeviceItemHoverOut}
                              placed={c.isPlaced}
                              subLabel={c.displayId}
                            />
                          </Box>
                        </Flex>
                      ))
                    ) : (
                      <NoCheckPointView
                        title={t('floors:checkpoint_no_placed')}
                      />
                    )}
                  </VStack>
                </Box>
              )}
              {unplacedCheckpoints.length > 0 && (
                <Box
                  animate={{ opacity: 1 }}
                  as={motion.div}
                  exit={{ opacity: 0 }}
                  initial={{ opacity: 0 }}
                  w='full'
                >
                  <Text
                    color='#44546F'
                    fontSize='16px'
                    fontWeight={500}
                    letterSpacing='-0.4px'
                    lineHeight='24px'
                    my='20px'
                  >
                    {t('floors:checkpoint_unplaced_label')}
                  </Text>
                  <VStack align='stretch' spacing='10px'>
                    {hasUnplacedCheckpoint ? (
                      unplacedCheckpoints.map((c) => (
                        <Flex alignItems='center' gap={2} key={c.id}>
                          <Checkbox
                            colorScheme='blue'
                            isChecked={isSelected(c.id)}
                            onChange={() => toggleCheckpoint(c.id)}
                            size='sm'
                          />
                          <Box flex={1}>
                            <UnPlacedCheckpointListItem
                              // Only want to drag Checkpoints out if we're in
                              // Edit Mode as we're gating all Floor Plan moving
                              // actions on that.
                              canDrag={isEditModeOn}
                              checkpoint={c}
                              isEditMode={isEditModeOn}
                              onClick={handleOnClick}
                              onTileDragStart={onTileDragStart}
                            />
                          </Box>
                        </Flex>
                      ))
                    ) : (
                      <NoCheckPointView
                        title={t('floors:checkpoint_no_unplaced')}
                      />
                    )}
                  </VStack>
                </Box>
              )}
            </>
          ) : (
            <Text
              animate={{ opacity: 1 }}
              as={motion.div}
              color='#9b9b9b'
              exit={{ opacity: 0 }}
              fontSize='14px'
              fontWeight={500}
              initial={{ opacity: 0 }}
              letterSpacing='-0.4px'
              lineHeight='16px'
              my='30px'
              textAlign='center'
            >
              {t('common:list_empty')}
            </Text>
          )}
        </AnimatePresence>
      </DrawerScrollContainer>
      {isModalOpen && (
        <CreateCheckPointsModal isOpen={isModalOpen} onClose={onModalClose} />
      )}
      {isPrintModalOpen && (
        <BulkPrintModal
          checkpoints={selectedCount > 0 ? selectedCheckpoints : checkpoints}
          facilityId={facilityId}
          isOpen={isPrintModalOpen}
          onClose={onPrintModalClose}
        />
      )}
    </>
  )
}
